import { useState, useCallback, useMemo, useRef } from "react";
import type { Message, ChatSummary } from "../types/chat";
import { formatDate } from "../utils/helpers";
import { useUser } from "../components/UserContext";
import { saveReplyLinkMock } from "../api/reply";
import { renameChat, getChatDetails } from "../api/chat";

export type MessagesMap = Record<string, Message[]>;

export interface ExtendedMessage extends Message {
  sqlQuery?: string;
  userMessageId?: string;
  isContextualReply?: boolean;
  backendId?: number;
  replyTo?: string;
  replyToMessageId?: number;
}

const normalizeBaseUrl = (raw?: string | null): string | undefined => {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  // Handle common broken/concatenated values like "...camngottps://api2..." by taking the last URL marker.
  const markers = ["https://", "http://", "ttps://", "ttp://"];
  let bestIdx = -1;
  let bestMarker = "";
  for (const marker of markers) {
    const idx = trimmed.lastIndexOf(marker);
    if (idx > bestIdx) {
      bestIdx = idx;
      bestMarker = marker;
    }
  }

  let candidate = bestIdx >= 0 ? trimmed.slice(bestIdx) : trimmed;
  if (bestMarker === "ttps://" || bestMarker === "ttp://") {
    candidate = `h${candidate}`;
  }

  try {
    // Validate and strip trailing slashes for stable joining.
    new URL(candidate);
    return candidate.replace(/\/+$/, "");
  } catch {
    return undefined;
  }
};

export const useChatMessages = (
  chat_id: number,
  externalId: string,
  messagesMap: MessagesMap,
  setMessagesMap: React.Dispatch<React.SetStateAction<MessagesMap>>,
  savedChats: ChatSummary[],
  setSavedChats: React.Dispatch<React.SetStateAction<ChatSummary[]>>,
  targetDbId: string,
  chatUniqueId: string
) => {
  const { user, language } = useUser();
  const inputRef = useRef<string>('');
  const [renderedInput, setRenderedInput] = useState<string>('');
  
  const messagePairCache = useRef(new Map());
  const lastMessagesVersion = useRef<number>(0);
  
  const setInput = (val: string) => {
    inputRef.current = val;
    setRenderedInput(val);
  };

  const [isLoading, setLoading] = useState(false);

  const messages = useMemo(
    () => messagesMap[chatUniqueId] || [],
    [messagesMap, chatUniqueId]
  );

  const messagesIndex = useMemo(() => {
    const index = new Map<string, Message>();
    messages.forEach(message => {
      index.set(message.id, message);
    });
    
    const currentVersion = messages.length + messages.reduce((acc, m) => acc + m.id.length, 0);
    if (currentVersion !== lastMessagesVersion.current) {
      messagePairCache.current.clear();
      lastMessagesVersion.current = currentVersion;
    }
    
    return index;
  }, [messages]);

  const getApiUrl = (): string => {
    const storedApiUrl = normalizeBaseUrl(localStorage.getItem("backend_api_url"));
    const envApiUrl = normalizeBaseUrl(process.env.REACT_APP_API_URL);
    return storedApiUrl || envApiUrl || "https://api.pandora-ai.it/auth";
  };

  const getLLMApiUrl = (): string => {
    const rawStored = localStorage.getItem("backend_llm_api_url");
    const storedLlmUrl = normalizeBaseUrl(rawStored);
    const envLlmUrl = normalizeBaseUrl(process.env.REACT_APP_API_LLM_URL);

    // If we have a broken persisted value, drop it so env can take over.
    if (!storedLlmUrl && rawStored) {
      localStorage.removeItem("backend_llm_api_url");
    }

    return storedLlmUrl || envLlmUrl || "https://api.pandora-ai.it/camngo";
  };
  
  const API = getApiUrl();
  const LLM_API = getLLMApiUrl();

  const pushMessage = useCallback((msg: Message | ExtendedMessage) => {
    setMessagesMap(prev => ({
      ...prev,
      [String(chatUniqueId)]: [...(prev[String(chatUniqueId)] || []), msg],
    }));
  }, [setMessagesMap, chatUniqueId]);

  const hasUserMessage = messages.some(m => m.type === "user");

  const sendMessage = useCallback(async (replyToId?: string, useWeb?: boolean) => {
    const text = inputRef.current.trim();
    if (!text || isLoading) return;
    setLoading(true);

    const userMessageId = crypto.randomUUID();
    
    // Resolve reply_to_message_id only from the selected message to avoid wrong/random IDs.
    let replyToMessageId: number | undefined = undefined;
    let replyMessage: Message | undefined = undefined;
    if (replyToId) {
      replyMessage = messages.find(m => m.id === replyToId);
      if (replyMessage && replyMessage.type === "bot") {
        const backendId = (replyMessage as any).backendId;
        if (typeof backendId === "number" && backendId > 0) {
          replyToMessageId = backendId;
        } else {
          const numericId = Number(replyMessage.id);
          if (!isNaN(numericId) && isFinite(numericId) && numericId > 0) {
            replyToMessageId = numericId;
          }
        }
      }

      if (replyToMessageId === undefined && chat_id && replyMessage) {
        try {
          const chatDetails = await getChatDetails(chat_id);
          const backendMessages = chatDetails?.messages || [];
          const replyText = replyMessage.text || replyMessage.explanation;

          if (replyText) {
            const matches = backendMessages.filter((m: any) => m?.role === "bot" && m?.content === replyText);
            if (matches.length > 0) {
              const latest = matches.reduce((a: any, b: any) => {
                if (!a?.created_at) return b;
                if (!b?.created_at) return a;
                return a.created_at > b.created_at ? a : b;
              });
              const numericBackendId = Number(latest?.id);
              if (!isNaN(numericBackendId) && isFinite(numericBackendId) && numericBackendId > 0) {
                replyToMessageId = numericBackendId;
              }
            }
          }

          if (replyToMessageId !== undefined) {
            setMessagesMap(prev => {
              const chatMessages = prev[String(chatUniqueId)] || [];
              const updatedMessages = chatMessages.map(msg =>
                msg.id === replyMessage!.id ? { ...msg, backendId: replyToMessageId } : msg
              );
              return {
                ...prev,
                [String(chatUniqueId)]: updatedMessages,
              };
            });
          }
        } catch (err) {
          console.warn('Failed to resolve replyToMessageId from backend:', err);
        }
      }
    }

    console.log('💾 REPLY ID FOR SAVING USER MESSAGE:', {
      replyToId,
      replyToMessageId,
      willBeSaved: replyToMessageId !== undefined && replyToMessageId > 0
    });
    
    // Crea il messaggio user con replyToMessageId (backendId verrà aggiunto dopo la risposta del backend)
    pushMessage({ 
      id: userMessageId, 
      text, 
      type: "user", 
      replyTo: replyToId,
      replyToMessageId: replyToMessageId,  // ⭐ Salva l'ID del backend del messaggio a cui risponde
      useWeb: useWeb === true
    });
    if (replyToId) {
      saveReplyLinkMock({
        chatId: String(chatUniqueId),
        messageId: userMessageId,
        replyToId,
        createdAt: new Date().toISOString(),
      }).catch(() => {});
    }
    setInput("");

    try {
      const token = localStorage.getItem("access_token");
      const headersBase: Record<string, string> = {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      };
      if (token) headersBase["Authorization"] = `Bearer ${token}`;
      if (chatUniqueId && chat_id) {
        const userMessageBody: Record<string, any> = {
          chat_id,
          role: "user",
          content: text,
        };
        if (replyToMessageId !== undefined && replyToMessageId > 0) {
          userMessageBody.reply_to_message_id = replyToMessageId;
        }
        
        console.log('💾 SAVING USER MESSAGE:', {
          replyToId,
          replyToMessageId,
          replyToMessageIdType: typeof replyToMessageId,
          willSaveReplyId: replyToMessageId !== undefined && replyToMessageId > 0,
          userMessageBody: JSON.stringify(userMessageBody, null, 2),
          allMessages: messages.map(m => ({ id: m.id, backendId: (m as any).backendId, type: m.type }))
        });
        
        const saveUserRes = await fetch(`${API}/storage/messages`, {
          method: "POST",
          headers: headersBase,
          body: JSON.stringify(userMessageBody),
        });
        if (saveUserRes.ok) {
          try {
            const savedUserData = await saveUserRes.json();
            const savedUserId = savedUserData?.id || savedUserData?.message_id;
            console.log('=== USER MESSAGE SAVED ===');
            console.log('User message saved with ID:', savedUserId || 'ID not returned');
            console.log('Frontend user message ID (UUID):', userMessageId);
            console.log('Full response:', savedUserData);
            console.log('Note: backend user_message_id will be available after LLM response');
            console.log('===========================');
          } catch (e) {
            console.log('User message saved (response not JSON)');
          }
        } else {
          const errorText = await saveUserRes.text().catch(() => "");
          console.error('Failed to save user message:', errorText);
        }
      }
      const API_TOKEN = process.env.REACT_APP_API_TOKEN || "cc44fb5e-2c28-4728-aeb4-b6439dc26f13";
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      console.log('Final reply_to_message_id to send:', replyToMessageId);
      console.log('Request body will include reply_to_message_id:', replyToMessageId !== undefined);
      console.log('========================');

      const requestBody: Record<string, any> = {
        chat_id: chatUniqueId,
        messaggio: text,
        timezone: timezone,
        user_id: user?.id || null,
        jwt_token: localStorage.getItem("access_token") || null,
        language: language,
      };

      // Add use_web flag if explicitly set to true
      if (useWeb === true) {
        requestBody.use_web = true;
        console.log('✅ use_web flag added to request');
      }

      // ВСЕГДА добавляем reply_to_message_id если он определен
      if (replyToMessageId !== undefined && replyToMessageId > 0) {
        requestBody.reply_to_message_id = replyToMessageId;
        console.log('✅ reply_to_message_id added to request:', replyToMessageId);
      }

      console.log('📤 Sending request to LLM API:', {
        url: `${LLM_API}/chat/${targetDbId}/`,
        llmApiUrl: LLM_API,
        reply_to_message_id: requestBody.reply_to_message_id,
        has_reply: !!requestBody.reply_to_message_id,
        replyToId: replyToId,
        requestBody: JSON.stringify(requestBody, null, 2)
      });

      // Create AbortController for timeout (3 minutes = 180000ms)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);

      let llmRes: Response;
      try {
        llmRes = await fetch(`${LLM_API}/chat/${targetDbId}/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${API_TOKEN}`,
            "ngrok-skip-browser-warning": "true"
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }

        const llm = await llmRes.json();
      
      // ⭐ Salva gli ID numerici restituiti dal backend
      const backendUserMessageId: number | undefined = llm?.user_message_id;
      const backendBotMessageId: number | undefined = llm?.bot_message_id;
      
      console.log('=== BACKEND RESPONSE IDs ===');
      console.log('user_message_id from backend (LLM response):', backendUserMessageId);
      console.log('bot_message_id from backend (LLM response):', backendBotMessageId);
      console.log('is_contextual_reply:', llm?.is_contextual_reply);
      console.log('Note: These IDs should match the IDs saved earlier in /storage/messages');
      console.log('===========================');
      
      // Check if the backend recognized this as a contextual reply
      const isContextualReply = llm?.is_contextual_reply === true;
      const usedWeb = useWeb === true || llm?.search_mode === true;
      
      const sqlQuery = llm?.sql ?? null;
      const isSearchMode = llm?.search_mode === true;
      if (isSearchMode && llm?.risultati && Array.isArray(llm.risultati)) {
        const webResults = llm.risultati;
        const rawWebExplanation = llm?.spiegazione;
        let webExplanation: string;
        
        if (typeof rawWebExplanation === 'string') {
          webExplanation = rawWebExplanation;
        } else if (rawWebExplanation && typeof rawWebExplanation === 'object' && 'question' in rawWebExplanation) {
          webExplanation = typeof rawWebExplanation.question === 'string' 
            ? rawWebExplanation.question 
            : "Ecco i risultati della ricerca web:";
        } else if (rawWebExplanation) {
          webExplanation = JSON.stringify(rawWebExplanation);
        } else {
          webExplanation = "Ecco i risultati della ricerca web:";
        }
        
        const botMessage: ExtendedMessage = {
          id: crypto.randomUUID(),
          type: "bot",
          text: webExplanation,
          timestamp: new Date().toISOString(),
          tableData: webResults.length > 0 ? {
            headers: Object.keys(webResults[0]),
            rows: webResults,
          } : undefined,
          explanation: webExplanation,
          isTableExpanded: false,
          sqlQuery: undefined,
          userMessageId: userMessageId,
          isContextualReply: isContextualReply,
          backendId: backendBotMessageId,  // ⭐ Salva l'ID numerico del backend
          replyToMessageId: isContextualReply && replyToMessageId ? replyToMessageId : undefined,  // ⭐ Salva replyToMessageId se è contextual reply
          useWeb: true
        };

        pushMessage(botMessage);
        
        // ⭐ Aggiorna il messaggio user con l'ID numerico del backend
        if (backendUserMessageId !== undefined) {
          setMessagesMap(prev => {
            const chatMessages = prev[String(chatUniqueId)] || [];
            const updatedMessages = chatMessages.map(msg => 
              msg.id === userMessageId 
                ? { ...msg, backendId: backendUserMessageId }
                : msg
            );
            return {
              ...prev,
              [String(chatUniqueId)]: updatedMessages,
            };
          });
        }

        const botMessageBody: Record<string, any> = {
          chat_id,
          role: "bot",
          content: webExplanation,
          output: {
            search_mode: true,
            result_preview: webResults,
            explanation: webExplanation,
          },
          sql: null,
        };
        if (isContextualReply && replyToMessageId !== undefined && replyToMessageId > 0) {
          botMessageBody.reply_to_message_id = replyToMessageId;
        }

        const saveBotRes = await fetch(`${API}/storage/messages`, {
          method: "POST",
          headers: headersBase,
          body: JSON.stringify(botMessageBody),
        });
        if (saveBotRes.ok) {
          try {
            const savedBotData = await saveBotRes.json();
            const savedBotId = savedBotData?.id || savedBotData?.message_id;
            console.log('=== BOT MESSAGE SAVED (SEARCH MODE) ===');
            console.log('Bot message saved with ID:', savedBotId || 'ID not returned');
            console.log('Backend bot_message_id from LLM response:', backendBotMessageId);
            console.log('Frontend bot message ID (UUID):', botMessage.id);
            console.log('Full response:', savedBotData);
            console.log('======================================');
            
            // ⭐ Se il backend ha restituito un ID diverso, aggiorna il messaggio
            if (savedBotId && savedBotId !== backendBotMessageId) {
              console.warn('⚠️ ID mismatch: savedBotId != backendBotMessageId');
            }
          } catch (e) {
            console.log('Bot message saved (response not JSON)');
          }
        } else {
          const errorText = await saveBotRes.text().catch(() => "");
          console.error('Failed to save bot message:', errorText);
        }
      } else {
        const rawSpiegazione = llm?.spiegazione;
        let normalizedExplanation: string | null = null;
        
        if (typeof rawSpiegazione === 'string') {
          normalizedExplanation = rawSpiegazione;
        } else if (rawSpiegazione && typeof rawSpiegazione === 'object') {
          if ('question' in rawSpiegazione && typeof rawSpiegazione.question === 'string') {
            normalizedExplanation = rawSpiegazione.question;
          } else {
            normalizedExplanation = JSON.stringify(rawSpiegazione);
          }
        }
        
        const rawPayload = llm?.payload ?? {
          sql: sqlQuery ?? null,
          result_preview: llm?.risultati ?? null,
          explanation: normalizedExplanation,
        };
        const { sql, ...payload } = rawPayload;

        const hasExplanation = !!payload.explanation;
        const hasTable = Array.isArray(payload.result_preview) && payload.result_preview.length > 0;

        const botText = hasExplanation
          ? payload.explanation
          : hasTable
            ? "Ecco i risultati trovati:"
            : "Mi dispiace, non ho trovato alcuna informazione utile.";

        const outputPayload = (hasExplanation || hasTable)
          ? payload
          : { type: "text", message: botText };

        const botMessage: ExtendedMessage = {
          id: crypto.randomUUID(),
          type: "bot",
          text: botText,
          timestamp: new Date().toISOString(),
          tableData: hasTable ? {
            headers: Object.keys(payload.result_preview[0]),
            rows: payload.result_preview,
          } : undefined,
          explanation: hasExplanation ? payload.explanation : undefined,
          isTableExpanded: false,
          sqlQuery: sqlQuery,
          userMessageId: userMessageId,
          isContextualReply: isContextualReply,
          backendId: backendBotMessageId,  // ⭐ Salva l'ID numerico del backend
          replyToMessageId: isContextualReply && replyToMessageId ? replyToMessageId : undefined,  // ⭐ Salva replyToMessageId se è contextual reply
          useWeb: usedWeb
        };

        pushMessage(botMessage);
        
        // ⭐ Aggiorna il messaggio user con l'ID numerico del backend
        if (backendUserMessageId !== undefined) {
          setMessagesMap(prev => {
            const chatMessages = prev[String(chatUniqueId)] || [];
            const updatedMessages = chatMessages.map(msg => 
              msg.id === userMessageId 
                ? { ...msg, backendId: backendUserMessageId }
                : msg
            );
            return {
              ...prev,
              [String(chatUniqueId)]: updatedMessages,
            };
          });
        }

        const botMessageBody: Record<string, any> = {
          chat_id,
          role: "bot",
          content: botText,
          output: {
            ...payload,
            sql: payload.sql ?? sqlQuery ?? undefined,
          },
          sql: sqlQuery ?? null,
        };
        if (isContextualReply && replyToMessageId !== undefined && replyToMessageId > 0) {
          botMessageBody.reply_to_message_id = replyToMessageId;
        }

        const saveBotRes = await fetch(`${API}/storage/messages`, {
          method: "POST",
          headers: headersBase,
          body: JSON.stringify(botMessageBody),
        });
        if (saveBotRes.ok) {
          try {
            const savedBotData = await saveBotRes.json();
            const savedBotId = savedBotData?.id || savedBotData?.message_id;
            console.log('=== BOT MESSAGE SAVED ===');
            console.log('Bot message saved with ID:', savedBotId || 'ID not returned');
            console.log('Backend bot_message_id from LLM response:', backendBotMessageId);
            console.log('Frontend bot message ID (UUID):', botMessage.id);
            console.log('Full response:', savedBotData);
            console.log('==========================');
            
            // ⭐ Se il backend ha restituito un ID diverso, aggiorna il messaggio
            if (savedBotId && savedBotId !== backendBotMessageId) {
              console.warn('⚠️ ID mismatch: savedBotId != backendBotMessageId');
            }
          } catch (e) {
            console.log('Bot message saved (response not JSON)');
          }
        } else {
          const errorText = await saveBotRes.text().catch(() => "");
          console.error('Failed to save bot message:', errorText);
        }
      }

      if (!hasUserMessage) {
        const res = await fetch(`${API}/api/generate-title`, {
          method: "POST",
          headers: headersBase,
          body: JSON.stringify({ text }),
        });
        const { title } = await res.json();
        const finalTitle = `${title} — ${targetDbId}`;
        const updatedChats = savedChats.map(c =>
          c.chatId === externalId ? { ...c, title: finalTitle || targetDbId } : c
        );
        setSavedChats(updatedChats);
        localStorage.setItem("savedChats", JSON.stringify(updatedChats));

        try {
          await renameChat(chat_id, finalTitle);
        } catch (err) {
          console.error("Failed to update chat title:", err);
        }
      }

    } catch (err) {
      let errorMessage = (err as Error).message;
      
      // Check if error is due to timeout
      if (err instanceof Error && err.name === 'AbortError') {
        errorMessage = 'Request timeout: The LLM response took too long (more than 3 minutes). Please try again.';
      }
      
      pushMessage({
        id: crypto.randomUUID(),
        text: `❌ ${errorMessage}`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [renderedInput, isLoading, chatUniqueId, targetDbId, pushMessage, savedChats, hasUserMessage, setSavedChats, chat_id, externalId, user, messages, language, API, LLM_API]);

  const getMessagePair = useCallback((botMessageId: string) => {
    if (messagePairCache.current.has(botMessageId)) {
      return messagePairCache.current.get(botMessageId);
    }

    const botMessage = messagesIndex.get(botMessageId);
    if (!botMessage) {
      messagePairCache.current.set(botMessageId, null);
      return null;
    }

    const extendedBot = botMessage as ExtendedMessage;
    if (!extendedBot.userMessageId) {
      messagePairCache.current.set(botMessageId, null);
      return null;
    }

    const userMessage = messagesIndex.get(extendedBot.userMessageId);
    if (!userMessage) {
      messagePairCache.current.set(botMessageId, null);
      return null;
    }

    const result = { userMessage, botMessage: extendedBot };
    messagePairCache.current.set(botMessageId, result);
    
    return result;
  }, [messagesIndex]);

  return { 
    input: renderedInput, 
    setInput, 
    messages, 
    isLoading, 
    sendMessage, 
    pushMessage,
    getMessagePair 
  };
};
